'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { usePathname } from 'next/navigation';
import { GripVertical, LucideIcon, Star, X } from 'lucide-react';

import { Button } from '@kit/ui/button';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@kit/ui/shadcn-sidebar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kit/ui/tooltip';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import { BrandLink } from '~/components/brand-link';
import { adminNavItemsByPath } from './admin-tab-context';

// Navigation item definition
interface NavItem {
  label: string;
  path: string;
  icon: LucideIcon;
}

// navItemsMap = single source of truth in admin-tab-context.tsx (Rule #8)
const navItemsMap = adminNavItemsByPath;

const STORAGE_KEY = 'nav-favorites';
const MAX_FAVORITES = 5;

// Get favorites from localStorage
function getFavorites(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

// Save favorites to localStorage
function saveFavorites(favorites: string[]) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
}

export function SidebarFavorites() {
  const pathname = usePathname();
  const favoritesContext = useFavoritesOptional();
  const [isEditing, setIsEditing] = useState(false);

  // Use context if available, otherwise fall back to localStorage
  const favorites = favoritesContext?.favorites ?? [];
  const toggleFavorite = favoritesContext?.toggleFavorite;

  // Convert paths to nav items - filter out undefined entries
  const favoriteItems: NavItem[] = favorites
    .map((path) => navItemsMap[path])
    .filter((item): item is NavItem => item !== undefined);

  if (favoriteItems.length === 0) {
    return null;
  }

  return (
    <SidebarGroup>
      <SidebarGroupLabel className="flex items-center justify-between">
        <span className="flex items-center gap-1">
          <Star className="h-3 w-3" />
          <Trans i18nKey="common:favorites" defaults="Favorites" />
        </span>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5"
                onClick={() => setIsEditing(!isEditing)}
              >
                {isEditing ? (
                  <X className="h-3 w-3" />
                ) : (
                  <GripVertical className="h-3 w-3" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {isEditing ? 'Done editing' : 'Edit favorites'}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {favoriteItems.map((item) => {
            const isActive =
              item.path === pathname ||
              (pathname.startsWith(item.path) && item.path !== '/admin');

            return (
              <SidebarMenuItem key={item.path}>
                <SidebarMenuButton
                  asChild
                  isActive={isActive}
                  className={cn(isEditing && 'pr-8')}
                >
                  <BrandLink to={item.path}>
                    <item.icon className="h-4 w-4" />
                    <span>{item.label}</span>
                  </BrandLink>
                </SidebarMenuButton>
                {isEditing && toggleFavorite && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-destructive absolute top-1/2 right-1 h-6 w-6 -translate-y-1/2"
                    onClick={() => toggleFavorite(item.path)}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

// Utility function to add a favorite (can be called from command palette or elsewhere)
export function addFavorite(path: string): boolean {
  const favorites = getFavorites();
  if (favorites.includes(path)) return false;
  if (favorites.length >= MAX_FAVORITES) return false;

  const newFavorites = [...favorites, path];
  saveFavorites(newFavorites);
  return true;
}

// Utility function to toggle a favorite
export function toggleFavorite(path: string): boolean {
  const favorites = getFavorites();
  if (favorites.includes(path)) {
    saveFavorites(favorites.filter((p) => p !== path));
    return false;
  } else if (favorites.length < MAX_FAVORITES) {
    saveFavorites([...favorites, path]);
    return true;
  }
  return false;
}

// Hook to check if a path is favorited
export function useIsFavorite(path: string): boolean {
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- post-mount localStorage hydration; required to avoid SSR mismatch
    setIsFavorite(getFavorites().includes(path));
  }, [path]);

  return isFavorite;
}

// ============================================================================
// FAVORITES CONTEXT - For shared state across components
// ============================================================================

interface FavoritesContextType {
  favorites: string[];
  isFavorite: (path: string) => boolean;
  toggleFavorite: (path: string) => boolean;
  canAddMore: boolean;
}

const FavoritesContext = createContext<FavoritesContextType | null>(null);

export function FavoritesProvider({ children }: { children: React.ReactNode }) {
  const [favorites, setFavorites] = useState<string[]>([]);

  // Load favorites on mount
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync from localStorage on mount
    setFavorites(getFavorites());
  }, []);

  const isFavorite = useCallback(
    (path: string) => favorites.includes(path),
    [favorites],
  );

  const handleToggleFavorite = useCallback(
    (path: string) => {
      setFavorites((prev) => {
        if (prev.includes(path)) {
          const newFavorites = prev.filter((p) => p !== path);
          saveFavorites(newFavorites);
          return newFavorites;
        } else if (prev.length < MAX_FAVORITES) {
          const newFavorites = [...prev, path];
          saveFavorites(newFavorites);
          return newFavorites;
        }
        return prev;
      });
      return !favorites.includes(path);
    },
    [favorites],
  );

  const canAddMore = favorites.length < MAX_FAVORITES;

  const value = useMemo(
    () => ({
      favorites,
      isFavorite,
      toggleFavorite: handleToggleFavorite,
      canAddMore,
    }),
    [favorites, isFavorite, handleToggleFavorite, canAddMore],
  );

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavoritesContext() {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error(
      'useFavoritesContext must be used within FavoritesProvider',
    );
  }
  return context;
}

// Optional hook that doesn't throw if not in provider (for gradual adoption)
export function useFavoritesOptional() {
  return useContext(FavoritesContext);
}
